import {ServiceType} from "../constants/ServiceType";
import {ChatItemType} from "../constants/ChatItemType";
import {CommonUtil} from "./CommonUtil";

export const PluginUtil = {
    serviceTypesSupported : [
        ServiceType.PAGE,
        ServiceType.PODCAST,
        ServiceType.VIDEO,
        ServiceType.WEBSITE,
        ServiceType.SSO_LOGIN
    ],
    chatFlowItemTypesSupported : [
        ChatItemType.MESSAGE,
        ChatItemType.ANSWER,
        ChatItemType.IMAGE,
        ChatItemType.ACTION,
        ChatItemType.CONDITION,
        ChatItemType.CHATFLOW,
        ChatItemType.TEXTFIELD,
        ChatItemType.SERVICE,
        ChatItemType.REDIRECT,
        ChatItemType.ANIMATED_SVG,
        ChatItemType.AI_REQUEST,
        ChatItemType.CONNECTOR
    ],
    configKeys : {
        CHAT_FLOW_ID : "chatFlowID",
        SOS_SERVICE_ID : "sosServiceID",
        MENU_SERVICE_ID : "menuServiceID",
        HEADER_THEME : "headerTheme",
        HEADER_BACKGROUND_COLOUR : "headerBackgroundColour",
        HEADER_TITLE : "headerTitle",
        DEFAULT_USER_ROLE_ID : "defaultUserRoleID",
        DEFAULT_LOCATION_ID : "defaultLocationID",
        INCOMING_EMOTION : "incomingEmotionId",
        WIDGET_ALIGNMENT : "widgetAlignment",
        WIDGET_BORDER_COLOUR : "widgetBorderColour",
        WIDGET_BACKGROUND_COLOUR : "widgetBackgroundColour",
        WIDGET_TEXT_COLOUR : "widgetTextColour",
        WIDGET_TITLE : "widgetTitle",
        WIDGET_SHOW_ICON : "widgetShowIcon",
        WIDGET_SHOW_EMOTIONS : "widgetShowEmotions",
        CHAT_FLOWS : "chatFlows",
        SUPPORT_SECTION_ID : "supportSectionID",
        SUPPORT_SECTION_TITLE : "supportSectionTitle",
        CHAT_SHOW_NAVIGATION : "chatShowNavigation",
        CHAT_SHOW_SOS : "chatShowSos",
        CHAT_SHOW_CHARLEY : "chatShowCharley",
        CHAT_SHOW_QR_LINK : "chatShowQRLink",
        CHAT_SHOW_OVERFLOW : "chatShowOverflow",
        WELCOME_SCREEN_SHOWN : "welcomeScreenShown",
        WELCOME_SCREEN_IMAGE_SHOWN : "welcomeScreenImageShown",
        WELCOME_SCREEN_MESSAGE : "welcomeScreenMessage",
        WELCOME_SCREEN_BUTTON_LABEL : "welcomeScreenButtonLabel",
        WIDGET_ASSISTANT_IMAGE_ID : "widgetAssistantImageID",
        WIDGET_ASSISTANT_IMAGE_URL : "widgetAssistantImageUrl",
        WELCOME_SCREEN_ASSISTANT_IMAGE_ID : "welcomeScreenAssistantImageID",
        WELCOME_SCREEN_ASSISTANT_IMAGE_URL : "welcomeScreenAssistantImageUrl",
        ALLOW_PERSISTENT_STORAGE : "allowPersistentStorage",
        WIDGET_EMBED_TYPE : "embedType",
        WIDGET_EMBED_POSITION : "embedPosition",
        POWERED_BY_ENABLED : "poweredByEnabled",
        POWERED_BY_LABEL : "poweredByLabel",
        POWERED_BY_URL : "poweredByUrl",
        CHAT_OPTIONS_MODE : "chatOptionsMode",
        SHOW_APP_HEADER : "showAppHeader",
        APP_THEME : "appTheme",
        APP_BACKGROUND_IMAGE_ID : "appBackgroundImageID",
        APP_BACKGROUND_IMAGE_URL : "appBackgroundImageUrl",
        APP_START_BACKGROUND_IMAGE_ID : "appStartBackgroundImageId",
        APP_START_BACKGROUND_IMAGE_URL : "appStartBackgroundImageUrl",
        START_SCREEN_IMAGE_ID : "startScreenImageID",
        START_SCREEN_IMAGE_URL : "startScreenImageUrl",
        START_SCREEN_TITLE_FIRST : "startScreenTitleFirst",
        START_SCREEN_SUBTITLE_FIRST : "startScreenSubtitleFirst",
        START_SCREEN_PROMPT_FIRST : "startScreenPromptFirst",
        START_SCREEN_TITLE_SECOND : "startScreenTitleSecond",
        START_SCREEN_SUBTITLE_SECOND : "startScreenSubtitleSecond",
        START_SCREEN_PROMPT_SECOND : "startScreenPromptSecond",
        CHAT_ASSISTANT_OPTION_IMAGE_ID : "chatAssistantOptionImageID",
        CHAT_ASSISTANT_OPTION_IMAGE_URL : "chatAssistantOptionImageUrl",
        CHAT_START_IMAGE_ID : "chatStartImageID",
        CHAT_START_IMAGE_URL : "chatStartImageURL",
        FULL_SCREEN_AT_STARTUP : "fullScreenAtStartup",
        CANVAS_ENABLED : "canvasEnabled",
        CANVAS_APP_NAME : "canvasAppName",
        CANVAS_ICON_IMAGE_ID : "canvasIconImageID",
        CANVAS_ICON_IMAGE_URL : "canvasIconImageUrl",
        CANVAS_PLACEMENTS : "canvasPlacements",
        CANVAS_OPEN_IN_NEW_TAB : "canvasOpenInNewTab",
        CANVAS_DESCRIPTION : "canvasDescription",
        CANVAS_LTI_ISSUER : "canvasLtiIssuer",
        CANVAS_LTI_CLIENT_ID : "canvasLtiClientId",
        CANVAS_LTI_DEPLOYMENT_ID : "canvasLtiDeploymentId",
        CANVAS_LTI_JWKS_URI : "canvasLtiJwksUri",
        CANVAS_LTI_AUTH_LOGIN_URI : "canvasLtiAuthLoginUri",
        CANVAS_LTI_TOKEN_URI : "canvasLtiTokenUri",
        PRIVACY_POLICY_URL : "global_privacyPolicyURL",
        TERMS_USE_URL : "global_termsUseURL"
    },
    embedTypes : {
        STATIC : 1,
        FLOATING : 2
    },
    embedPositions : {
        TOP_LEFT : 1,
        TOP_RIGHT : 2,
        BOTTOM_RIGHT : 3,
        BOTTOM_LEFT : 4
    },
    chatOptionsModes : {
        NO_HEADER : 1,
        SHOW_IN_HEADER : 2,
        SHOW_IN_ASSISTANT_BUTTON : 3
    },
    checkChatFlowCompatibility : (data) => {
        let compatible = true;
        const results = [];

        if (data && Array.isArray(data)) {
            data.forEach((item) => {
                let innerResult = PluginUtil._checkChatFlowCompatibilityRecursive(item, results);
                if (!innerResult) {
                    compatible = false;
                }
            });
        }

        return {
            compatible,
            results
        };
    },
    _checkChatFlowCompatibilityRecursive(item, results) {
        let passed = true;

        if (item) {
            if (item.hasOwnProperty("typeID")) {
                const typeID = parseInt(item.typeID);
                if (!PluginUtil.chatFlowItemTypesSupported.includes(typeID)) {
                    passed = false;
                    results.push({
                        id : item.uuid,
                        title : CommonUtil.getOrDefault(item, "nodeText", "N/A"),
                        message : "Unsupported Chat Flow item: " + ChatItemType.getName(typeID)
                    });
                }

                if (typeID === ChatItemType.SERVICE) {
                    const service = CommonUtil.getOrDefault(item, "service", null);
                    if (service) {
                        if (service.hasOwnProperty("typeID")) {
                            const serviceTypeID = parseInt(service.typeID);
                            if (!PluginUtil.serviceTypesSupported.includes(serviceTypeID)) {
                                passed = false;
                                results.push({
                                    id : item.uuid,
                                    title : CommonUtil.getOrDefault(item, "nodeText", "N/A"),
                                    message : ServiceType.getTitle(serviceTypeID) + " is not supported"
                                });
                            }
                        }
                    }
                }
            }

            if (item.hasOwnProperty("children") && Array.isArray(item.children)) {
                item.children.forEach((child) => {
                    let innerResult = PluginUtil._checkChatFlowCompatibilityRecursive(child, results);
                    if (!innerResult) {
                        passed = false;
                    }
                });
            }
        }

        return passed;
    },
    _lastSupportPageTableMode : 1
}
